import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, Save, CalendarClock } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { BRAND_COLORS, withAlpha } from "../lib/bookingStatus";
import { deDate, deDateTime } from "../lib/dateFormat";
import { deCurrency } from "../lib/numberFormat";

export default function TeamServicePackages() {
  const { effectiveRole } = useAuth();
  const isAdmin = effectiveRole === "admin";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState([]);
  const [priceInputs, setPriceInputs] = useState({});
  const [currency, setCurrency] = useState("EUR");
  const [saving, setSaving] = useState(false);
  const [overview, setOverview] = useState(null);
  const [remindingId, setRemindingId] = useState(null);
  const [remindedIds, setRemindedIds] = useState([]);

  const sendReminder = async (id) => {
    setRemindingId(id);
    try {
      await api.post(`/service-packages/${id}/send-renewal-reminder`);
      setRemindedIds((s) => [...s, id]);
      toast.success("Verlängerungs-Erinnerung gesendet.");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setRemindingId(null);
    }
  };

  const loadOverview = async () => {
    try {
      const { data } = await api.get("/service-packages/overview", { params: { days: 30 } });
      setOverview(data || { expiring: [], cancelled: [] });
    } catch (e) { console.debug("service-packages/overview load failed:", e); }
  };

  const loadBookings = async () => {
    try {
      const { data } = await api.get("/service-packages/bookings");
      setRows(data || []);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };
  const loadCatalog = async () => {
    try {
      const { data } = await api.get("/service-packages/catalog");
      setCatalog(data || []);
      const map = {};
      (data || []).forEach((p) => { map[p.id] = p.price == null ? "" : String(p.price); });
      setPriceInputs(map);
      if ((data || [])[0]?.currency) setCurrency(data[0].currency);
    } catch (e) { console.debug("service-packages/catalog load failed:", e); }
  };
  useEffect(() => { loadBookings(); loadCatalog(); loadOverview(); }, []);

  const savePrices = async () => {
    if (!catalog.length) {
      toast.error("Preise konnten nicht geladen werden. Bitte Seite neu laden.");
      return;
    }
    setSaving(true);
    try {
      const prices = {};
      for (const [id, val] of Object.entries(priceInputs)) {
        const t = (val ?? "").toString().trim().replace(",", ".");
        if (t === "") { prices[id] = null; continue; }
        const num = Number(t);
        if (Number.isNaN(num) || num < 0) {
          toast.error("Bitte gültige Preise eingeben (keine negativen Werte).");
          setSaving(false);
          return;
        }
        prices[id] = num;
      }
      await api.put("/service-packages/prices", { prices, currency });
      toast.success("Preise gespeichert.");
      await loadCatalog();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="team-service-packages">
      <div className="mb-10">
        <div className="label-caps mb-3">Betreuung</div>
        <h1 className="font-display text-4xl md:text-5xl text-navy">Service Pakete</h1>
        <p className="text-[15px] text-navy-dark/70 mt-3 max-w-2xl">
          Preise zentral pflegen (einheitlich für alle Kunden und Objekte) und verbindliche
          Buchungen Ihrer Kunden einsehen.
        </p>
      </div>

      {/* Ablauf-/Kündigungs-Übersicht — neutrale Monitoring-Sektion, damit das
          Team rechtzeitig eine nahtlose Verlängerung anbieten kann. Nicht rot. */}
      {overview && (overview.expiring?.length > 0 || overview.cancelled?.length > 0) && (
        <section
          className="mb-10 bg-white border-l-2 p-6 md:p-7"
          style={{ borderColor: BRAND_COLORS.gold, background: withAlpha(BRAND_COLORS.gold, 0.05) }}
          data-testid="service-packages-overview"
        >
          <div className="flex items-center gap-2.5 mb-1">
            <CalendarClock className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.8} />
            <div className="label-caps">Läuft bald aus · Verlängerung anbieten</div>
          </div>
          <p className="text-[13px] text-navy-dark/70 mb-5">
            Endliche Pakete, deren Betreuung in den nächsten 30 Tagen endet (inkl. gekündigter). Bieten Sie
            rechtzeitig eine nahtlose Verlängerung ab dem Anschlussdatum an.
          </p>

          <ul className="space-y-2" data-testid="service-packages-overview-list">
            {[...(overview.expiring || []), ...(overview.cancelled || [])]
              .sort((a, b) => (a.end_date < b.end_date ? -1 : 1))
              .map((it) => {
                const soon = it.days_left <= 7;
                return (
                  <li
                    key={it.id}
                    data-testid={`service-packages-overview-row-${it.id}`}
                    className="border border-navy/10 bg-white px-4 py-3 flex items-center gap-3 flex-wrap md:flex-nowrap"
                    style={{ borderLeft: `3px solid ${soon ? BRAND_COLORS.gold : withAlpha(BRAND_COLORS.navy, 0.2)}` }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] text-navy font-medium truncate">
                        {it.customer_name}{it.property_name ? ` · ${it.property_name}` : ""}
                      </div>
                      <div className="text-[12px] text-navy-dark/70 mt-0.5 truncate">
                        <span className="font-medium">{it.package_title}</span>
                        {it.status === "cancelled" && <span className="italic"> · gekündigt</span>}
                        {" "}· endet {deDate(it.end_date)}
                        {" · "}nahtloser Anschluss ab {deDate(it.seamless_start)}
                      </div>
                    </div>
                    <span
                      className="shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold tracking-[0.12em] uppercase"
                      style={{
                        background: soon ? BRAND_COLORS.gold : withAlpha(BRAND_COLORS.navy, 0.08),
                        color: soon ? BRAND_COLORS.navy : BRAND_COLORS.navy,
                      }}
                      data-testid={`service-packages-overview-daysleft-${it.id}`}
                    >
                      {it.days_left <= 0 ? "endet heute" : `in ${it.days_left} ${it.days_left === 1 ? "Tag" : "Tagen"}`}
                    </span>
                    <button
                      type="button"
                      data-testid={`service-packages-overview-remind-${it.id}`}
                      onClick={() => sendReminder(it.id)}
                      disabled={remindingId === it.id || remindedIds.includes(it.id)}
                      className="shrink-0 inline-flex items-center gap-1.5 border px-3 py-1.5 text-[10px] tracking-[0.12em] uppercase transition-colors hover:bg-gold/10 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ borderColor: BRAND_COLORS.gold, color: BRAND_COLORS.navy }}
                    >
                      {remindedIds.includes(it.id) ? "Erinnert ✓" : remindingId === it.id ? "Sendet …" : "Jetzt erinnern"}
                    </button>
                  </li>
                );
              })}
          </ul>
        </section>
      )}

      {/* Preis-Editor — Admin-only. Preise gelten einheitlich. */}
      <section className="mb-10 bg-white border border-navy/10 p-6 md:p-7" data-testid="service-packages-price-editor">
        <div className="flex items-center gap-2.5 mb-5">
          <Package className="w-4 h-4 text-navy-dark/60" strokeWidth={1.8} />
          <div className="label-caps">Preise (einheitlich für alle Kunden &amp; Objekte)</div>
        </div>

        <div className="space-y-3">
          {catalog.map((p) => (
            <div
              key={p.id}
              data-testid={`service-package-price-row-${p.id}`}
              className="flex items-center gap-4 flex-wrap md:flex-nowrap border border-navy/10 px-4 py-3"
            >
              <div className="min-w-0 flex-1">
                <div className="label-caps">{p.title}</div>
                <div className="text-[13px] text-navy mt-0.5">{p.main}</div>
              </div>
              {isAdmin ? (
                <div className="flex items-center gap-2 shrink-0">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    value={priceInputs[p.id] ?? ""}
                    onChange={(e) => setPriceInputs((cur) => ({ ...cur, [p.id]: e.target.value }))}
                    placeholder="—"
                    data-testid={`service-package-price-input-${p.id}`}
                    className="w-32 border border-navy/20 px-3 py-2 text-[15px] text-navy text-right focus:border-gold focus:outline-none"
                  />
                  <span className="text-[13px] text-navy-dark/60">{currency}</span>
                </div>
              ) : (
                <div className="text-[15px] font-display text-navy shrink-0" data-testid={`service-package-price-readonly-${p.id}`}>
                  {p.price == null ? "Preis auf Anfrage" : deCurrency(p.price, p.currency || "EUR")}
                </div>
              )}
            </div>
          ))}
        </div>

        {isAdmin && (
          <div className="mt-5 flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={savePrices}
              disabled={saving || !catalog.length}
              data-testid="service-packages-price-save"
              className="inline-flex items-center gap-2 text-ivory px-6 py-3 text-[11.5px] tracking-[0.18em] uppercase hover:opacity-90 transition-opacity disabled:opacity-50"
              style={{ background: BRAND_COLORS.navy }}
            >
              <Save className="w-4 h-4" strokeWidth={1.8} />
              {saving ? "Wird gespeichert …" : "Preise speichern"}
            </button>
            <span className="text-[12px] text-navy-dark/55">
              Leeres Feld = „Preis auf Anfrage". Abrechnungszyklus monatlich.
            </span>
          </div>
        )}
      </section>

      <div className="label-caps mb-3">Buchungen</div>
      {loading ? (
        <div className="text-[13px] text-navy-dark/60">Wird geladen …</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-navy/10 p-16 text-center">
          <div className="font-display text-2xl text-navy mb-2">Noch keine Buchungen</div>
          <p className="text-[13px] text-navy-dark/60">
            Sobald ein Kunde ein Service-Paket verbindlich bucht, erscheint es hier.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div
              key={r.id}
              data-testid={`service-package-booking-${r.id}`}
              className="bg-white border border-navy/10 p-5 md:p-6 flex items-start justify-between gap-4 flex-wrap"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1.5">
                  <Package className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.5} />
                  <div className="label-caps">{r.package_title}</div>
                </div>
                <h3 className="font-display text-xl text-navy leading-tight">{r.package_main}</h3>
                <p className="text-[13px] text-navy-dark/70 mt-1">
                  {r.customer_name}{r.property_name ? ` · ${r.property_name}` : ""}
                </p>
                <p className="text-[12px] text-navy-dark/50 italic mt-0.5">{r.package_note}</p>
                {r.start_date && (
                  <p className="text-[12px] text-navy-dark/70 mt-1" data-testid={`service-package-booking-period-${r.id}`}>
                    {r.end_date
                      ? `Betreuung ${deDate(r.start_date)} – ${deDate(r.end_date)}`
                      : `Betreuung ab ${deDate(r.start_date)} · fortlaufend`}
                  </p>
                )}
              </div>
              <div className="text-right shrink-0">
                {r.package_price != null && (
                  <div className="font-display text-lg text-navy" data-testid={`service-package-booking-price-${r.id}`}>
                    {deCurrency(r.package_price, r.currency || "EUR")}
                  </div>
                )}
                <div className="text-[12px] text-navy-dark/60">{deDateTime(r.created_at)}</div>
                <div className="mt-1 inline-flex items-center gap-1.5 text-[10.5px] tracking-[0.18em] uppercase text-gold">
                  <span className="w-1.5 h-1.5" style={{ background: BRAND_COLORS.gold }} />
                  PIN-signiert
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
